// Polyfill for `@international/mastodon-i18n@0.2.6`'s `i18n.getCountries()`
// and `i18n.getCountry()`, which internally call:
//
//   require(`i18n-iso-countries/langs/${language}.json`)
//
// at every invocation. Webpack handled this via its dynamic-`require`
// support; esbuild (used by Vite's dep optimizer) cannot statically
// resolve a require with a runtime variable, so it leaves a runtime stub
// that throws "Dynamic require of … is not supported" the moment a
// component renders the international login form.
//
// Rather than patching the library on disk (`patch-package`) or writing
// a custom esbuild plugin, we eagerly import the locale JSON files this
// app actually uses (matching `src/lib/translations/*.json`), register
// them with `i18n-iso-countries` once, and replace the two affected
// methods with implementations that have the same return shape but no
// `require()` call.
//
// Importing this module for side effects (top of `src/index.tsx`) before
// `initializeTranslations()` runs ensures every render path sees the
// patched methods.
//
// If `@international/mastodon-i18n` is upgraded past 0.2.6, re-verify
// that the deep import path below still resolves and that the override
// logic still matches the upstream implementation.

import { i18n } from '@international/mastodon-i18n';
import mastodonCountries from '@international/mastodon-i18n/build/i18n/countries';
import countries from 'i18n-iso-countries';

import en from 'i18n-iso-countries/langs/en.json';
import es from 'i18n-iso-countries/langs/es.json';
import fr from 'i18n-iso-countries/langs/fr.json';
import de from 'i18n-iso-countries/langs/de.json';

countries.registerLocale(en);
countries.registerLocale(es);
countries.registerLocale(fr);
countries.registerLocale(de);

const FALLBACK_LANGUAGE = 'en';

const resolveLanguage = (): string => {
  const locale = (
    i18n as unknown as { getLocale: () => string | undefined }
  ).getLocale?.();
  return locale?.slice(0, 2) || FALLBACK_LANGUAGE;
};

type CountryEntry = { value: string; name: string };

// Iterate over the same curated list mastodon-i18n's `getCountries` uses
// internally, so the order and the country set match the pre-migration
// behavior exactly. The list is a CommonJS default export with the shape
// `[{ name, value, alpha3, numeric, i18n? }, …]`.
const curatedCountries: Array<{ value?: string }> =
  (mastodonCountries as { default?: Array<{ value?: string }> }).default ??
  (mastodonCountries as unknown as Array<{ value?: string }>);

const patched = i18n as unknown as {
  getCountries: () => CountryEntry[];
  getCountry: (country: string) => string;
};

patched.getCountries = (): CountryEntry[] => {
  const language = resolveLanguage();
  const list: CountryEntry[] = [];
  for (const c of curatedCountries) {
    if (c?.value) {
      const localizedName =
        countries.getName(c.value, language) ||
        countries.getName(c.value, FALLBACK_LANGUAGE) ||
        c.value;
      list.push({ value: c.value, name: localizedName });
    }
  }
  return list;
};

patched.getCountry = (country: string): string => {
  const language = resolveLanguage();
  return (
    countries.getName(country, language) ||
    countries.getName(country, FALLBACK_LANGUAGE) ||
    country
  );
};

export {};
